import { useState } from "react";
import { motion } from "framer-motion";
import { MapPin } from "lucide-react";
import { DESTINATIONS, REGIONS, slugify } from "../data/site";

const fadeUp = {
  hidden: { opacity: 0, y: 24 },
  show: { opacity: 1, y: 0 },
};

export default function Destinations() {
  const [region, setRegion] = useState("All");
  const list =
    region === "All" ? DESTINATIONS : DESTINATIONS.filter((d) => d.region === region);

  return (
    <div data-testid="destinations-page" className="pt-[72px]">
      <section className="noise-dark bg-navy py-20 lg:py-28">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
          <p className="text-xs font-bold uppercase tracking-[0.25em] text-gold">
            Destinations
          </p>
          <h1 className="mt-5 max-w-3xl font-display text-4xl font-extrabold tracking-tighter text-white sm:text-5xl lg:text-6xl">
            Where do you want to go?
          </h1>
          <p className="mt-6 max-w-2xl text-base leading-relaxed text-white/80 sm:text-lg">
            Experts in all international destinations — from the classics of
            Europe to safaris in Africa. Tell us the vibe, and we&apos;ll tell
            you the perfect place and the best time to visit.
          </p>
        </div>
      </section>

      <section className="mx-auto max-w-7xl px-4 py-16 sm:px-6 lg:px-8 lg:py-24">
        <div className="no-scrollbar flex gap-2 overflow-x-auto pb-2">
          {REGIONS.map((r) => (
            <button
              key={r}
              onClick={() => setRegion(r)}
              data-testid={`region-filter-${slugify(r)}`}
              className={`shrink-0 rounded-full px-5 py-2.5 font-display text-sm font-bold transition-colors duration-200 ${
                region === r
                  ? "bg-navy text-white"
                  : "border border-black/10 bg-white text-gray-700 hover:border-navy hover:text-navy"
              }`}
            >
              {r}
            </button>
          ))}
        </div>

        <div className="mt-10 grid grid-cols-1 gap-5 sm:grid-cols-2 lg:grid-cols-3">
          {list.map((d, i) => (
            <motion.div
              key={d.name}
              variants={fadeUp}
              initial="hidden"
              whileInView="show"
              viewport={{ once: true, amount: 0.15 }}
              transition={{ duration: 0.4, delay: (i % 3) * 0.07 }}
              data-testid={`destination-card-${slugify(d.name)}`}
              className="group relative aspect-[4/5] overflow-hidden rounded-2xl ring-1 ring-black/5"
            >
              <img
                src={d.img}
                alt={d.name}
                loading="lazy"
                className="absolute inset-0 h-full w-full object-cover transition-transform duration-500 group-hover:scale-105"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-black/65 via-black/10 to-transparent" />
              <div className="absolute left-5 top-5">
                <span className="rounded-full bg-white/90 px-3.5 py-1.5 text-xs font-bold uppercase tracking-wider text-navy backdrop-blur-sm">
                  {d.region}
                </span>
              </div>
              <div className="absolute bottom-0 left-0 p-6">
                <h3 className="font-display text-2xl font-bold text-white">
                  {d.name}
                </h3>
                <p className="mt-1 text-sm text-white/85">{d.blurb}</p>
              </div>
            </motion.div>
          ))}
        </div>

        <div className="mt-16 rounded-3xl border border-black/5 bg-white p-8 text-center sm:p-12">
          <MapPin className="mx-auto h-9 w-9 text-coral" />
          <h2 className="mt-4 font-display text-2xl font-bold tracking-tight text-navy sm:text-3xl">
            Not sure which one fits you?
          </h2>
          <p className="mx-auto mt-3 max-w-xl text-base text-gray-600">
            We&apos;ll help you determine the perfect location, the best time
            to visit, and nearby countries or cities worth clubbing into the
            same trip.
          </p>
          <a
            href={BRAND_WHATSAPP}
            target="_blank"
            rel="noopener noreferrer"
            data-testid="destinations-whatsapp-cta"
            className="mt-7 inline-flex rounded-full bg-gold px-8 py-4 font-display font-bold text-navy transition-transform duration-200 hover:scale-[1.03]"
          >
            Ask on WhatsApp
          </a>
        </div>
      </section>
    </div>
  );
}

const BRAND_WHATSAPP =
  "https://wa.me/919702045551?text=Hi%20Sagar!%20I%20want%20to%20plan%20a%20holiday%20with%20Manvi%20Travels.";
